// react-google-maps/api documentation: https://web.archive.org/web/20230701010714/https://react-google-maps-api-docs.netlify.app/
import { GoogleMap, Marker, MarkerClusterer, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useState } from 'react';
import colors from '../ui/colors';
import { mapStyle } from './Map';
import { post } from '../mocks/postsMocks/postsStore';
import UI from '../ui';
import PostImage, { getPostImageUrls } from './PostImage';
import { Paper } from '@mui/material';
import { useNavigate } from 'react-router';
import PickIcon from './PickIcon';
import { ISOToCountry } from '../functions/isoToCountry';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const containerStyle = {
  width: '100%',
  height: '360px',
  marginTop: '30px',
};

const center = {
  lat: 22.54992, lng: 0,
};

type polygonPath = {
  lat: number
  lng: number
}[];

type regionPath = { region: polygonPath[], color?: string };

export const googleConfig = {
  id: undefined,
  googleMapsApiKey: API_KEY,
  libraries: ['places' as const, 'marker' as const, 'visualization' as const],
};

const InteractiveMap: React.FC<{ shadedRegions?: regionPath[], posts: post[] }> = ({ shadedRegions = [], posts }) => {
  const { isLoaded } = useJsApiLoader(googleConfig);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [activePin, setActivePin] = useState<post | null>(null);
  const navigate = useNavigate();

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMarkerClick: (post: post) => void = (post) => {
    if (map) {
      const offsetLng = 1;
      map.panTo({ lat: post.location.lat, lng: post.location.lng - offsetLng });
      map.setZoom(7);
    }
    setActivePin(post);
  };

  return isLoaded
    ? (
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={1}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          styles: mapStyle,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          cameraControl: false,
        }}
      >
        {shadedRegions.map(({ color, region }, i) => (
          <Polygon
            key={i}
            paths={region}
            options={{
              strokeOpacity: 0,
              strokeWeight: 0,
              fillColor: color || colors.chartreuse,
            }}
          />
        ))}
        <MarkerClusterer
          onClick={() => { }}
          averageCenter
          enableRetinaIcons
          gridSize={60}
          styles={[
            {
              url: "/icons/circle.svg",
              height: 30,
              width: 30,
              textColor: "#fff",
              textSize: 12,
            }
          ]}
        >
          {clusterer =>
            // @ts-ignore This compiles fine despite the type error. Probably just bad typing?
            posts.map((post, i) => (
              <Marker
                key={i}
                icon={{
                  url: "/icons/place.svg",
                  fillColor: 'red',
                  scaledSize: new window.google.maps.Size(40, 40)
                }}
                onClick={() => handleMarkerClick(post)}
                position={{ lat: post.location.lat, lng: post.location.lng }}
                clusterer={clusterer} />
            ))}
        </MarkerClusterer>

        {activePin && (
          <Paper sx={{ //TODO: move paper to UI layer
            position: "absolute",
            left: "5%",
            top: "50%",
            transform: "translateY(-50%)",
            borderRadius: "5px",
            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
            width: '50%',
            maxHeight: '300px'
          }}>
            <UI.VerticalLayout gap={5}>
              <div style={{ position: 'absolute', padding: '2px', right: '0%' }}>
                <UI.IconButton
                  name='close'
                  onClick={() => setActivePin(null)}
                  color='white'
                />
              </div>
              <img
                src={getPostImageUrls(activePin)[0]}
                style={{
                  width: '100%',
                  height: '180px',
                  objectFit: 'cover',
                  borderTopLeftRadius: '5px',
                  borderTopRightRadius: '5px',
                }}
              />
              <div style={{ padding: '5px' }}>
                <UI.HorizontalLayout justify='space-between'>
                  <UI.HorizontalLayout gap={5} align="center">
                    {activePin.pick && <PickIcon animate post={activePin} />}
                    <UI.Text>{`${activePin.hotelName}, ${activePin.location.city}, ${ISOToCountry(activePin.location.country_code)}`}</UI.Text>
                  </UI.HorizontalLayout>
                  <UI.Button
                    label="View"
                    onClick={() => navigate(`/posts/${activePin.id}`)}
                  />
                </UI.HorizontalLayout>
              </div>
            </UI.VerticalLayout>
          </Paper>
        )}
      </GoogleMap>
    )
    : <></>;
};

export default InteractiveMap;
